/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { MuiColorInput } from 'mui-color-input';
// form
import { useForm } from 'react-hook-form';
// @mui
import {
    Box, Button, Typography, IconButton, Divider, Grid,
    Accordion, AccordionSummary, AccordionDetails,
    useMediaQuery, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// components
import { Iconify, Image } from '../../../components';
// hook-form
import { FormProvider, RHFTextField } from '../../../components/hook-form';
// redux
import { dispatch } from '../../../redux/store';
import { changeUserDetails } from '../../../redux/slices/practiceUser';
import { updateError } from '../../../redux/slices/user';
// settings components
import SettingsSection from '../../../components/settings/SettingsSection';
import StickySaveBar from '../../../components/settings/StickySaveBar';
// hooks
import useDirtyForm from '../../../hooks/useDirtyForm';
// style
import { clearButton } from '../../../styles/EmailMessageComposeStyle';
import { imageBox } from '../../../styles/brandingStyle';

// ----------------------------------------------------------------------

// eslint-disable-next-line no-unused-vars
function ImageUploadSlot({ label, type, inputRef, file, onChoose, onRemove, disabled }) {
    return (
        <Box
            sx={{
                border: '1px dashed #e2e8f0',
                borderRadius: '12px',
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                bgcolor: '#fafafa',
            }}
        >
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{label}</Typography>
            {file ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Image alt={label} src={file} sx={{ ...imageBox, flexShrink: 0 }} />
                    {!disabled && (
                        <IconButton
                            size="small"
                            color="error"
                            sx={{ ...clearButton, ml: 0 }}
                            onClick={onRemove}
                        >
                            <Iconify icon="basil:cross-outline" height={16} width={16} />
                        </IconButton>
                    )}
                </Box>
            ) : (
                <Box
                    onClick={disabled ? undefined : onChoose}
                    sx={{
                        height: 56,
                        border: '1px dashed #cbd5e1',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: disabled ? 'default' : 'pointer',
                        color: '#94a3b8',
                        fontSize: 24,
                        '&:hover': { bgcolor: disabled ? 'transparent' : alpha('#3b82f6', 0.04) },
                    }}
                >
                    <Iconify icon="eva:plus-fill" width={22} />
                </Box>
            )}
            <Button
                size="small"
                variant="outlined"
                onClick={onChoose}
                disabled={disabled}
                sx={{ textTransform: 'none', fontSize: 11, borderRadius: '8px', py: 0.5 }}
            >
                {file ? 'Change' : 'Choose file'}
            </Button>
            <input
                type="file"
                ref={inputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                    if (e.target.files?.[0]) {
                        const reader = new FileReader();
                        reader.readAsDataURL(e.target.files[0]);
                        reader.onload = () => onChoose(reader.result, true);
                    }
                }}
            />
        </Box>
    );
}

ImageUploadSlot.propTypes = {
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    inputRef: PropTypes.object.isRequired,
    file: PropTypes.string,
    onChoose: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

ImageUploadSlot.defaultProps = {
    file: null,
    disabled: false,
};

// ----------------------------------------------------------------------

const SOCIAL_ICONS = {
    FACEBOOK: { icon: 'mdi:facebook', color: '#1877F2' },
    INSTAGRAM: { icon: 'mdi:instagram', color: '#E4405F' },
    TWITTER: { icon: 'mdi:twitter', color: '#000' },
    YOUTUBE: { icon: 'mdi:youtube', color: '#FF0000' },
    TIKTOK: { icon: 'ic:baseline-tiktok', color: '#000' },
    GOOGLE_BUSINESS_REVIEW: { icon: 'mdi:google', color: '#4285F4' },
};

function LiveEmailPreview({ color, logoFile, footerBannerFile, bannerText, practiceData }) {
    const brandColor = color || '#00A76F';
    const name = practiceData?.name || 'Your Practice';
    const phone = practiceData?.contact?.phone || '';
    const email = practiceData?.contact?.email || '';
    const website = practiceData?.contact?.website || '';
    const address = practiceData?.address || {};
    const socials = (practiceData?.social_medias || []).filter((s) => s.value);

    const addressLine = [address.address_line_1, address.city, address.postcode].filter(Boolean).join(', ');

    return (
        <Box
            sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                overflow: 'hidden',
                bgcolor: '#fff',
            }}
        >
            {/* Email client chrome */}
            <Box sx={{ bgcolor: '#fff', px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5, borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f87171' }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fbbf24' }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#34d399' }} />
                <Typography sx={{ fontSize: 10, color: '#94a3b8', ml: 1 }}>Email Preview</Typography>
            </Box>

            {/* Email content */}
            <Box sx={{ bgcolor: '#fff', p: 0 }}>
                <Box sx={{ bgcolor: '#fff', overflow: 'hidden' }}>

                    {/* Header — logo on white */}
                    <Box
                        sx={{
                            bgcolor: '#fff',
                            px: 2.5,
                            py: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 52,
                            borderBottom: '1px solid #e2e8f0',
                        }}
                    >
                        {logoFile ? (
                            <Box
                                component="img"
                                src={logoFile}
                                alt="Logo"
                                sx={{ maxHeight: 44, maxWidth: 180, objectFit: 'contain' }}
                            />
                        ) : (
                            <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: 16, letterSpacing: '0.02em' }}>
                                {name}
                            </Typography>
                        )}
                    </Box>

                    {/* Banner text strip */}
                    {bannerText && (
                        <Box sx={{ bgcolor: alpha(brandColor, 0.06), px: 2.5, py: 1, borderBottom: `1px solid ${alpha(brandColor, 0.1)}` }}>
                            <Typography sx={{ fontSize: 11, color: '#475569', textAlign: 'center' }}>{bannerText}</Typography>
                        </Box>
                    )}

                    {/* Email body */}
                    <Box sx={{ px: 2.5, py: 2 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b', mb: 0.75 }}>
                            Dear Sarah,
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1, lineHeight: 1.7 }}>
                            Thank you for choosing {name} for your dental care. We&apos;re pleased to confirm your upcoming appointment.
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1.5, lineHeight: 1.7 }}>
                            Your consultation is scheduled for <strong>Monday 21st April at 10:00 AM</strong>. Please arrive 10 minutes early to complete any required paperwork.
                        </Typography>

                        {/* CTA button */}
                        <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                            <Box
                                sx={{
                                    display: 'inline-block',
                                    bgcolor: brandColor,
                                    color: '#fff',
                                    px: 3,
                                    py: 1,
                                    borderRadius: '8px',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    letterSpacing: '0.02em',
                                    cursor: 'default',
                                }}
                            >
                                Confirm Appointment
                            </Box>
                        </Box>

                        <Typography sx={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', lineHeight: 1.6 }}>
                            If you need to reschedule, please call us on {phone || '0123 456 7890'} or reply to this email.
                        </Typography>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ mx: 2.5 }}><Divider /></Box>

                    {/* Footer */}
                    <Box sx={{ px: 2.5, py: 2, textAlign: 'center' }}>
                        {/* Footer banner image */}
                        {footerBannerFile && (
                            <Box sx={{ mb: 1.5 }}>
                                <Box
                                    component="img"
                                    src={footerBannerFile}
                                    alt="Footer"
                                    sx={{ maxWidth: '100%', maxHeight: 60, objectFit: 'contain', borderRadius: '4px' }}
                                />
                            </Box>
                        )}

                        {/* Practice name + address */}
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e293b', mb: 0.25 }}>
                            {name}
                        </Typography>
                        {addressLine && (
                            <Typography sx={{ fontSize: 10, color: '#94a3b8', mb: 0.5 }}>
                                {addressLine}
                            </Typography>
                        )}

                        {/* Contact row */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                            {phone && (
                                <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                                    <Iconify icon="eva:phone-fill" width={10} sx={{ mr: 0.3, verticalAlign: 'middle' }} />
                                    {phone}
                                </Typography>
                            )}
                            {email && (
                                <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                                    <Iconify icon="eva:email-fill" width={10} sx={{ mr: 0.3, verticalAlign: 'middle' }} />
                                    {email}
                                </Typography>
                            )}
                            {website && (
                                <Typography sx={{ fontSize: 10, color: brandColor }}>
                                    <Iconify icon="eva:globe-2-fill" width={10} sx={{ mr: 0.3, verticalAlign: 'middle' }} />
                                    {website}
                                </Typography>
                            )}
                        </Box>

                        {/* Social icons */}
                        {socials.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
                                {socials.map((s) => {
                                    const meta = SOCIAL_ICONS[s.name];
                                    if (!meta) return null;
                                    return (
                                        <Box
                                            key={s.name}
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                bgcolor: alpha(meta.color, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Iconify icon={meta.icon} width={13} sx={{ color: meta.color }} />
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}

                        {/* Unsubscribe */}
                        <Typography sx={{ fontSize: 9, color: '#cbd5e1' }}>
                            You received this email because you are a patient of {name}.
                            <br />
                            <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>Unsubscribe</span> &bull; <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>Privacy Policy</span>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

LiveEmailPreview.propTypes = {
    color: PropTypes.string,
    logoFile: PropTypes.string,
    footerBannerFile: PropTypes.string,
    bannerText: PropTypes.string,
    practiceData: PropTypes.object,
};

LiveEmailPreview.defaultProps = {
    practiceData: {},
    footerBannerFile: null,
    color: '',
    logoFile: null,
    bannerText: '',
};

// ----------------------------------------------------------------------

export default function PracticeBranding({ branding, practiceId, practiceData, canEdit }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [inputFiles, setInputFiles] = useState([]);
    const [color, setColor] = useState(branding?.brand_colour || '');
    const [saving, setSaving] = useState(false);

    const primary = useRef();
    const secondary = useRef();
    const emailLogo = useRef();
    const emailFooterLogo = useRef();

    const defaultValues = {
        textContent: branding?.banner_text || '',
        bannerEnabled: branding?.is_banner_enabled || false,
    };

    const methods = useForm({ defaultValues });
    const { handleSubmit, watch, reset } = methods;
    const bannerText = watch('textContent');

    const { isDirty, markDirty, markClean } = useDirtyForm(defaultValues);

    // Warn user on page leave when there are unsaved changes
    useEffect(() => {
        const handler = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    useEffect(() => {
        if (branding !== null && Object.values(branding || {})?.length) {
            const newObj = [];
            if (branding?.primary_banner?.length) newObj.push({ type: 'primary', file: branding.primary_banner });
            if (branding?.secondary_banner?.length) newObj.push({ type: 'secondary', file: branding.secondary_banner });
            if (branding?.email_logo?.length) newObj.push({ type: 'emailLogo', file: branding.email_logo });
            if (branding?.email_footer_banner?.length) newObj.push({ type: 'emailFooter', file: branding.email_footer_banner });
            setInputFiles(newObj);
            setColor(branding?.brand_colour || '');
            reset({
                textContent: branding?.banner_text || '',
                bannerEnabled: branding?.is_banner_enabled || false,
            });
        }
    }, [branding]);

    const filterImages = (type) => inputFiles.filter((el) => el.type === type);

    const fetchData = async (url) => {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    };

    const handleInputFiles = (eOrResult, typeOrRemove, isDirectResult) => {
        if (isDirectResult) {
            // Called from ImageUploadSlot with base64 result directly
            const type = typeOrRemove;
            const filtered = inputFiles.filter((el) => el.type !== type);
            setInputFiles([...filtered, { type, file: eOrResult }]);
            markDirty();
            return;
        }
        // Called as remove: handleInputFiles(ref, type, 'remove')
        if (typeOrRemove === 'remove') {
            const type = isDirectResult; // isDirectResult used as type here when removing
            // eslint-disable-next-line no-param-reassign
            const filtered = inputFiles.filter((el) => el.type !== type);
            setInputFiles(filtered);
            markDirty();
            return;
        }
        // Legacy: called with event
        const type = typeOrRemove;
        const files = eOrResult.target.files[0];
        const reader = new FileReader();
        reader.readAsDataURL(files);
        reader.onload = () => {
            const filteredData = inputFiles.filter((el) => el.type === type);
            if (filteredData?.length > 0) {
                const updatedArray = inputFiles.filter((obj) => obj.type !== type);
                setInputFiles([...updatedArray, { type, file: reader.result }]);
            } else {
                setInputFiles([...inputFiles, { type, file: reader.result }]);
            }
            markDirty();
        };
    };

    const handleRemoveFile = (ref, type) => {
        const filtered = inputFiles.filter((el) => el.type !== type);
        setInputFiles(filtered);
        if (ref.current) ref.current.value = '';
        markDirty();
    };

    const handleColorChange = (newValue) => {
        setColor(newValue);
        markDirty();
    };

    const onSubmit = async (data) => {
        try {
            setSaving(true);
            const payload = {
                ...(branding?.id ? { id: branding.id } : {}),
                is_banner_enabled: data.bannerEnabled,
                primary_banner: (filterImages('primary')[0]?.file?.includes('https')
                    ? await fetchData(filterImages('primary')[0]?.file)
                    : filterImages('primary')[0]?.file) || '',
                secondary_banner: (filterImages('secondary')[0]?.file?.includes('https')
                    ? await fetchData(filterImages('secondary')[0]?.file)
                    : filterImages('secondary')[0]?.file) || '',
                email_logo: (filterImages('emailLogo')[0]?.file?.includes('https')
                    ? await fetchData(filterImages('emailLogo')[0]?.file)
                    : filterImages('emailLogo')[0]?.file) || '',
                email_footer_banner: (filterImages('emailFooter')[0]?.file?.includes('https')
                    ? await fetchData(filterImages('emailFooter')[0]?.file)
                    : filterImages('emailFooter')[0]?.file) || '',
            };
            if (color) payload.brand_colour = color;
            if (data?.textContent?.length > 0) payload.banner_text = data.textContent;
            if (Object.values(payload)?.length) {
                dispatch(changeUserDetails({ branding: payload }, practiceId, 'practice-branding'));
            }
            markClean();
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        } finally {
            setSaving(false);
        }
    };

    const handleDiscard = () => {
        setColor(branding?.brand_colour || '');
        const newObj = [];
        if (branding?.primary_banner?.length) newObj.push({ type: 'primary', file: branding.primary_banner });
        if (branding?.secondary_banner?.length) newObj.push({ type: 'secondary', file: branding.secondary_banner });
        if (branding?.email_logo?.length) newObj.push({ type: 'emailLogo', file: branding.email_logo });
        if (branding?.email_footer_banner?.length) newObj.push({ type: 'emailFooter', file: branding.email_footer_banner });
        setInputFiles(newObj);
        reset(defaultValues);
        markClean();
    };

    const logoFile = filterImages('emailLogo')[0]?.file || null;

    const previewSection = (
        <Box>
            <LiveEmailPreview
                color={color}
                logoFile={logoFile}
                footerBannerFile={filterImages('emailFooter')[0]?.file || null}
                bannerText={bannerText}
                practiceData={practiceData}
            />
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center', mt: 1 }}
            >
                Preview updates live as you edit
            </Typography>
        </Box>
    );

    return (
        <>
            <SettingsSection
                id="branding"
                icon="solar:palette-bold-duotone"
                title="Branding"
                description="Manage banners, logos, and brand colour"
                iconColor="#f59e0b"
            >
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={3}>
                        {/* Left: Settings */}
                        <Grid item xs={12} md={7}>
                            {/* Brand colour */}
                            <Box sx={{ mb: 2 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475569', mb: 1 }}>
                                    Brand Colour
                                </Typography>
                                <MuiColorInput
                                    format="hex"
                                    value={color}
                                    onChange={canEdit ? handleColorChange : undefined}
                                    isAlphaHidden
                                    disabled={!canEdit}
                                />
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            {/* Banner toggle + text */}
                            <Box sx={{ mb: 2 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475569', mb: 1 }}>
                                    Banner
                                </Typography>
                                <Box sx={{ mb: 1.5 }}>
                                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            {...methods.register('bannerEnabled', {
                                                onChange: () => canEdit && markDirty(),
                                            })}
                                            disabled={!canEdit}
                                            style={{ width: 16, height: 16, accentColor: '#00A76F' }}
                                        />
                                        <Typography sx={{ fontSize: 13, color: '#334155' }}>
                                            Enable banner
                                        </Typography>
                                    </label>
                                </Box>
                                <RHFTextField
                                    name="textContent"
                                    multiline
                                    rows={3}
                                    label="Banner text"
                                    placeholder="Enter banner text…"
                                    onChange={() => canEdit && markDirty()}
                                    disabled={!canEdit}
                                />
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            {/* Image uploads 2x2 grid */}
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475569', mb: 1.5 }}>
                                Images
                            </Typography>
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={6}>
                                    <ImageUploadSlot
                                        label="Primary Banner"
                                        type="primary"
                                        inputRef={primary}
                                        file={filterImages('primary')[0]?.file || null}
                                        onChoose={(resultOrEvent, isDirect) => {
                                            if (isDirect) {
                                                handleInputFiles(resultOrEvent, 'primary', true);
                                            } else {
                                                primary.current?.click();
                                            }
                                        }}
                                        onRemove={() => handleRemoveFile(primary, 'primary')}
                                        disabled={!canEdit}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ImageUploadSlot
                                        label="Secondary Banner"
                                        type="secondary"
                                        inputRef={secondary}
                                        file={filterImages('secondary')[0]?.file || null}
                                        onChoose={(resultOrEvent, isDirect) => {
                                            if (isDirect) {
                                                handleInputFiles(resultOrEvent, 'secondary', true);
                                            } else {
                                                secondary.current?.click();
                                            }
                                        }}
                                        onRemove={() => handleRemoveFile(secondary, 'secondary')}
                                        disabled={!canEdit}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ImageUploadSlot
                                        label="Email Logo"
                                        type="emailLogo"
                                        inputRef={emailLogo}
                                        file={filterImages('emailLogo')[0]?.file || null}
                                        onChoose={(resultOrEvent, isDirect) => {
                                            if (isDirect) {
                                                handleInputFiles(resultOrEvent, 'emailLogo', true);
                                            } else {
                                                emailLogo.current?.click();
                                            }
                                        }}
                                        onRemove={() => handleRemoveFile(emailLogo, 'emailLogo')}
                                        disabled={!canEdit}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ImageUploadSlot
                                        label="Email Footer"
                                        type="emailFooter"
                                        inputRef={emailFooterLogo}
                                        file={filterImages('emailFooter')[0]?.file || null}
                                        onChoose={(resultOrEvent, isDirect) => {
                                            if (isDirect) {
                                                handleInputFiles(resultOrEvent, 'emailFooter', true);
                                            } else {
                                                emailFooterLogo.current?.click();
                                            }
                                        }}
                                        onRemove={() => handleRemoveFile(emailFooterLogo, 'emailFooter')}
                                        disabled={!canEdit}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Right: Live Preview */}
                        <Grid item xs={12} md={5}>
                            {isMobile ? (
                                <Accordion
                                    disableGutters
                                    sx={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px !important',
                                        boxShadow: 'none',
                                        '&:before': { display: 'none' },
                                    }}
                                >
                                    <AccordionSummary expandIcon={<Iconify icon="eva:chevron-down-fill" />}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                                            Show preview
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>{previewSection}</AccordionDetails>
                                </Accordion>
                            ) : (
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 80,
                                    }}
                                >
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475569', mb: 1.5 }}>
                                        Email Preview
                                    </Typography>
                                    {previewSection}
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </FormProvider>
            </SettingsSection>

            {canEdit && (
                <StickySaveBar
                    isDirty={isDirty}
                    onSave={handleSubmit(onSubmit)}
                    onDiscard={handleDiscard}
                    saving={saving}
                />
            )}
        </>
    );
}

PracticeBranding.propTypes = {
    branding: PropTypes.object,
    practiceId: PropTypes.any,
    practiceData: PropTypes.object,
    canEdit: PropTypes.bool,
};

PracticeBranding.defaultProps = {
    branding: null,
    practiceId: null,
    practiceData: {},
    canEdit: false,
};
